import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ChevronRight, ContentCopy, Delete, Edit, KeyboardArrowDown, Search } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { CrudModal } from "@/components/crud/CrudModal";
import usePagination from "@/hooks/pagination";
import { useCrud } from "@/hooks/useCrud";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import {
  CATEGORIA_TODAS,
  deleteContribuicao,
  listCategorias,
  listContribuicoes,
  localizarCategorias,
  saveContribuicao,
  type CategoriaMercadologica,
  type Contribuicao as ContribuicaoApi,
} from "@/api/metas.service";
import { normalizar } from "./metasFormato";

// Tela de apoio ao cálculo das metas: cadastros de contribuição — o quanto
// cada categoria contribui, em percentual. Cada cadastro vale para um mês de
// referência (como o mês da meta do cenário) e é sempre uma configuração
// geral do cliente, sem escopo por loja, persistido pelo Product Runtime
// (metas.list_contribuicoes / save_contribuicao / delete_contribuicao). A
// hierarquia de mercadologia vem do ERP pela consulta do próprio módulo
// (metas.mercadologia), carregada nível a nível. Cadastro novo e
// independente por ora: nada ainda lê esse percentual para o cálculo de metas.

/** O que se sabe de cada nó já carregado da mercadologia. */
type Indice = Record<string, { nome: string; folha: boolean; pais: string[] }>;

/** A árvore do ERP como a tela a consome: carregada sob demanda, um nível por vez. */
interface Mercadologia {
  raiz: CategoriaMercadologica[];
  filhos: Record<string, CategoriaMercadologica[]>;
  carregandoRaiz: boolean;
  carregandoFilhos: Record<string, boolean>;
  indice: Indice;
  carregarFilhos: (id: string) => void;
  /** Resolve ids salvos que a árvore ainda não carregou — categoria a alguns níveis do topo, indexada direto pelo código (ver `localizarCategorias`). */
  resolverCategorias: (ids: string[]) => void;
}

/** Chave usada quando o cadastro vale para toda a árvore. */
const TODAS = CATEGORIA_TODAS;

/** Regra como o formulário a manipula: o percentual é o texto do campo. */
interface Regra {
  percentual: string;
  observacoes: string;
}

/**
 * Cadastro como a tela trabalha: as regras ficam indexadas pela categoria (o
 * back-end as troca como lista) e `categorias` guarda a seleção da hierarquia.
 */
interface Contribuicao {
  id: string;
  nome: string;
  ativo: boolean;
  /** Mês de referência do cadastro, em "AAAA-MM" — o back-end troca ano e mês separados. */
  mesReferencia: string;
  escopoCategorias: "todas" | "especificas";
  categorias: string[];
  regras: Record<string, Regra>;
}

type Rascunho = Omit<Contribuicao, "id">;

const REGRA_PADRAO: Regra = { percentual: "0", observacoes: "" };

// Referências estáveis: o formulário recalcula `rascunhoVazio()` a cada
// render (o mês precisa ser o atual), e um array/objeto novo aqui faria os
// efeitos que dependem de `categorias`/`regras` disparar em loop infinito.
const CATEGORIAS_VAZIAS: string[] = [];
const REGRAS_VAZIAS: Record<string, Regra> = {};

/** Cadastro novo: já aberto no mês corrente, como o cenário faz com o mês da meta. */
function rascunhoVazio(): Rascunho {
  return {
    nome: "",
    ativo: true,
    mesReferencia: mesAtual(),
    escopoCategorias: "todas",
    categorias: CATEGORIAS_VAZIAS,
    regras: REGRAS_VAZIAS,
  };
}

const CHAVE_MES = /^\d{4}-\d{2}$/;

function chaveDe(ano: number, mes: number) {
  return `${ano}-${String(mes).padStart(2, "0")}`;
}

function partesDe(chave: string) {
  const [ano = 0, mes = 1] = chave.split("-").map(Number);
  return { ano, mes };
}

/** "AAAA-MM" para o objeto que o DatePicker espera, ou null se vazio/inválido. */
function paraDayjs(chave: string): Dayjs | null {
  if (!CHAVE_MES.test(chave)) return null;
  const { ano, mes } = partesDe(chave);
  return dayjs(new Date(ano, mes - 1, 1));
}

/** O que o DatePicker devolve, de volta para "AAAA-MM". */
function deDayjs(data: Dayjs | null) {
  return data && data.isValid() ? chaveDe(data.year(), data.month() + 1) : "";
}

function mesAtual() {
  const hoje = new Date();
  return chaveDe(hoje.getFullYear(), hoje.getMonth() + 1);
}

function rotuloMes(chave: string) {
  if (!CHAVE_MES.test(chave)) return "—";
  const { ano, mes } = partesDe(chave);
  return new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

/** Converte o cadastro gravado no back-end para o formato do formulário. */
function daApi(contribuicao: ContribuicaoApi): Contribuicao {
  const categorias = contribuicao.escopoCategorias === "especificas" ? contribuicao.regras.map((r) => r.categoriaId) : [];
  return {
    id: contribuicao.id ?? "",
    nome: contribuicao.nome,
    ativo: contribuicao.ativo,
    mesReferencia: chaveDe(contribuicao.anoReferencia, contribuicao.mesReferencia),
    escopoCategorias: contribuicao.escopoCategorias,
    categorias,
    regras: Object.fromEntries(
      contribuicao.regras.map((r) => [r.categoriaId, { percentual: String(r.percentual), observacoes: r.observacoes }]),
    ),
  };
}

/** Converte o formulário para o formato do back-end: só as regras dos ids em escopo são enviadas (as demais ficariam órfãs). */
function paraApi(rascunho: Rascunho, id?: string): ContribuicaoApi {
  const { ano, mes } = partesDe(rascunho.mesReferencia);
  return {
    ...(id ? { id } : {}),
    nome: rascunho.nome.trim(),
    ativo: rascunho.ativo,
    anoReferencia: ano,
    mesReferencia: mes,
    escopoCategorias: rascunho.escopoCategorias,
    regras: idsComRegra(rascunho).map((categoriaId) => {
      const regra = regraDe(rascunho, categoriaId);
      return {
        categoriaId,
        percentual: Number(String(regra.percentual).replace(",", ".")),
        observacoes: regra.observacoes,
      };
    }),
  };
}


function caminhoDe(indice: Indice, pais: string[]) {
  return pais.map((p) => indice[p]?.nome ?? p).join(" › ");
}

/** Ancestrais de um nó; vazio para um id cujo nível ainda não foi carregado. */
function ancestraisDe(indice: Indice, id: string) {
  return indice[id]?.pais ?? [];
}

// A seleção da hierarquia vai só até o N2: produtos e níveis mais fundos do
// ERP não entram no cadastro de contribuição.
const PROFUNDIDADE_MAXIMA_SELECAO = 2;

// Um id gravado pode ainda não ter sido carregado do ERP (a árvore vem nível a
// nível) ou ter saído da mercadologia: nesse caso o próprio código é o rótulo,
// em vez de a regra sumir da tela.
function nomeCategoria(indice: Indice, id: string) {
  if (id === TODAS) return "Todas as categorias e produtos";
  return indice[id]?.nome ?? id;
}

function rotuloPercentual(regra: Regra) {
  const n = Number(regra.percentual);
  return `${n.toLocaleString("pt-BR")}%`;
}

/** Ids que recebem regra própria: a árvore inteira ou cada seleção da hierarquia. */
function idsComRegra(c: Rascunho) {
  return c.escopoCategorias === "todas" ? [TODAS] : c.categorias;
}

function regraDe(c: Rascunho, id: string) {
  return c.regras[id] ?? REGRA_PADRAO;
}

/** Resumo de uma linha da listagem: com várias regras o detalhe fica no modal. */
function rotuloInfluencia(c: Rascunho, indice: Indice) {
  const ids = idsComRegra(c);
  if (ids.length === 0) return "Nenhuma regra definida";
  if (ids.length === 1) return `${nomeCategoria(indice, ids[0]!)}: ${rotuloPercentual(regraDe(c, ids[0]!))}`;
  return `${ids.length} categorias com regra`;
}

function validar(c: Rascunho, indice: Indice) {
  const problemas: string[] = [];
  if (!c.nome.trim()) problemas.push("Informe o nome do cadastro.");
  if (!CHAVE_MES.test(c.mesReferencia)) problemas.push("Informe o mês de referência do cadastro.");
  if (c.escopoCategorias === "especificas" && c.categorias.length === 0)
    problemas.push("Selecione pelo menos uma categoria ou produto.");
  idsComRegra(c).forEach((id) => {
    const regra = regraDe(c, id);
    // Duas casas decimais no máximo: é o que a coluna do back-end guarda.
    const texto = String(regra.percentual).trim().replace(",", ".");
    const n = Number(texto);
    if (!/^\d+(\.\d{1,2})?$/.test(texto) || n < 0 || n > 100)
      problemas.push(`Informe um percentual de contribuição entre 0 e 100, com até duas casas, para ${nomeCategoria(indice, id)}.`);
  });
  return problemas;
}

interface LinhaRegraProps {
  nome: string;
  regra: Regra;
  habilitada: boolean;
  herdada: boolean;
  onChange: (patch: Partial<Regra>) => void;
}

function CelulasRegra({ nome, regra, habilitada, herdada, onChange }: LinhaRegraProps) {
  return (
    <>
      <TableCell sx={{ minWidth: 150 }}>
        <TextField
          type="text"
          inputMode="decimal"
          size="small"
          fullWidth
          label="Contribuição (%)"
          slotProps={{ inputLabel: { shrink: true } }}
          disabled={!habilitada}
          value={habilitada ? regra.percentual : ""}
          placeholder="—"
          onChange={(ev) => {
            const valor = ev.target.value;
            if (!/^\d*[.,]?\d{0,2}$/.test(valor)) return;
            onChange({ percentual: valor });
          }}
          aria-label={`Contribuição percentual — ${nome}`}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          label="Observações"
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }}
          disabled={!habilitada}
          value={habilitada ? regra.observacoes : ""}
          placeholder={
            herdada ? "Usa a regra da categoria superior" : !habilitada ? "Selecione a categoria" : "Observações desta categoria"
          }
          onChange={(ev) => onChange({ observacoes: ev.target.value })}
          aria-label={`Observações — ${nome}`}
        />
      </TableCell>
    </>
  );
}

interface ContribuicaoFormProps {
  values: Partial<Contribuicao>;
  setValues: Dispatch<SetStateAction<Partial<Contribuicao>>>;
  isEditing: boolean;
  mercadologia: Mercadologia;
  erros: string[];
  onFecharErros: () => void;
}

function ContribuicaoForm({ values, setValues, mercadologia, erros, onFecharErros }: ContribuicaoFormProps) {
  const form: Rascunho = { ...rascunhoVazio(), ...values };
  const { raiz, filhos, carregandoRaiz, carregandoFilhos, indice, carregarFilhos, resolverCategorias } = mercadologia;

  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [filtroCategorias, setFiltroCategorias] = useState("");

  const selecionadas = useMemo(() => new Set(form.categorias), [form.categorias]);

  // Abrir o modal em edição (ou marcar uma categoria) revela os ancestrais já
  // conhecidos — sem isso a seleção salva ficaria escondida em níveis recolhidos.
  const categoriasSelecionadas = form.categorias;
  useEffect(() => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      categoriasSelecionadas.forEach((c) => ancestraisDe(indice, c).forEach((p) => next.add(p)));
      return next;
    });
  }, [categoriasSelecionadas, indice]);

  // Um id salvo cujo nível ninguém abriu ainda não tem entrada no índice —
  // sem isso a linha ficaria com o código cru no lugar do nome. Só entra na
  // busca quem falta mesmo: uma vez indexado (aqui ou por navegação manual),
  // some da lista e o efeito não pede de novo.
  useEffect(() => {
    const desconhecidas = categoriasSelecionadas.filter((c) => !indice[c]);
    if (desconhecidas.length > 0) resolverCategorias(desconhecidas);
  }, [categoriasSelecionadas, indice, resolverCategorias]);

  // Um nível só é buscado no ERP quando alguém o abre — inclusive quando quem
  // abriu foi o efeito acima, e não um clique no expansor. Além do N2 a busca
  // para: a seleção não desce até produto.
  useEffect(() => {
    expandidos.forEach((id) => {
      const profundidade = ancestraisDe(indice, id).length + 1;
      if (filhos[id] === undefined && !indice[id]?.folha && profundidade < PROFUNDIDADE_MAXIMA_SELECAO) carregarFilhos(id);
    });
  }, [expandidos, filhos, indice, carregarFilhos]);

  // Com filtro a árvore vira uma lista achatada: cada resultado traz o caminho
  // completo, poupando o usuário de expandir nível a nível até o produto. Só os
  // níveis já carregados entram na busca — a consulta do ERP não filtra por nome.
  const categoriasEncontradas = useMemo(() => {
    const termo = normalizar(filtroCategorias);
    if (!termo) return null;
    return Object.entries(indice)
      .filter(([, info]) => normalizar(info.nome).includes(termo))
      .map(([id, info]) => ({ id, ...info }));
  }, [filtroCategorias, indice]);

  /** Ids que a árvore mostra agora: a raiz mais os filhos dos nós expandidos. */
  const idsNaArvore = useMemo(() => {
    const ids = new Set<string>();
    const percorrer = (ns: CategoriaMercadologica[]) =>
      ns.forEach((n) => {
        ids.add(n.id);
        if (expandidos.has(n.id)) percorrer(filhos[n.id] ?? []);
      });
    percorrer(raiz);
    return ids;
  }, [raiz, filhos, expandidos]);

  // Uma seleção gravada em um nível que ainda não foi aberto (ou que saiu da
  // mercadologia) não aparece na árvore: essas linhas vêm antes dela para que a
  // regra continue visível e editável.
  const selecionadasForaDaArvore = form.categorias.filter((id) => !idsNaArvore.has(id));

  const patch = (valores: Partial<Contribuicao>) => setValues((v) => ({ ...v, ...valores }));

  const alterarRegra = (id: string, valores: Partial<Regra>) =>
    setValues((v) => {
      const regras = v.regras ?? {};
      return { ...v, regras: { ...regras, [id]: { ...(regras[id] ?? REGRA_PADRAO), ...valores } } };
    });

  const alternarExpandido = (id: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const alternarCategoria = (id: string, marcada: boolean) =>
    setValues((v) => {
      // Marcar uma categoria absorve os descendentes já marcados: a regra da
      // categoria vale para toda a subárvore.
      const atuais = v.categorias ?? [];
      const restantes = atuais.filter((c) => c !== id && !ancestraisDe(indice, c).includes(id));
      return { ...v, categorias: marcada ? [...restantes, id] : restantes };
    });

  /** Uma linha da tabela de regras. Com `caminho` a linha é um resultado de
   *  busca (ou uma seleção fora da árvore): sem recuo nem expansor, e com a
   *  trilha de ancestrais abaixo do nome. */
  const linhaCategoria = (id: string, caminho?: string) => {
    const info = indice[id];
    const pais = info?.pais ?? [];
    const nome = nomeCategoria(indice, id);
    const depth = pais.length + 1;
    const folha = info?.folha ?? false;
    const herdada = pais.some((p) => selecionadas.has(p));
    const propria = selecionadas.has(id);
    const indeterminada = !propria && !herdada && form.categorias.some((c) => ancestraisDe(indice, c).includes(id));
    const expansivel = !folha && depth < PROFUNDIDADE_MAXIMA_SELECAO;
    const expandido = expansivel && expandidos.has(id);
    const plano = caminho !== undefined;

    return (
      <TableRow key={id} sx={propria ? { bgcolor: "background.subtle" } : undefined}>
        <TableCell sx={{ minWidth: 280 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ pl: plano ? 0 : `${(depth - 1) * 16}px` }}
          >
            {!plano && (
              <IconButton
                size="small"
                disabled={!expansivel}
                onClick={() => alternarExpandido(id)}
                aria-label={`Expandir ou recolher ${nome}`}
                aria-expanded={expansivel ? expandido : undefined}
                sx={{ visibility: expansivel ? "visible" : "hidden" }}
              >
                {expandido ? <KeyboardArrowDown fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            )}
            <Box sx={{ minWidth: 0 }}>
              <FormControlLabel
                sx={{ mr: 0 }}
                label={nome}
                control={
                  <Checkbox
                    size="small"
                    checked={propria || herdada}
                    indeterminate={indeterminada}
                    disabled={herdada}
                    onChange={(ev) => alternarCategoria(id, ev.target.checked)}
                  />
                }
              />
              {plano && caminho && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: "34px" }}>
                  {caminho}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={folha ? "Produto" : `N${depth}`}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          </Stack>
        </TableCell>
        <CelulasRegra
          nome={nome}
          regra={regraDe(form, id)}
          habilitada={propria}
          herdada={herdada}
          onChange={(valores) => alterarRegra(id, valores)}
        />
      </TableRow>
    );
  };

  /** Espera pelo nível que está sendo buscado no ERP, no recuo do pai. */
  const linhaCarregando = (id: string, depth: number) => (
    <TableRow key={`carregando-${id}`}>
      <TableCell colSpan={3} sx={{ pl: `${depth * 16 + 16}px`, color: "text.secondary" }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={14} />
          <Typography variant="caption">Carregando subcategorias…</Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const linhasHierarquia = (ns: CategoriaMercadologica[], depth = 1): React.ReactNode[] =>
    ns.flatMap((n) => {
      const linhas: React.ReactNode[] = [linhaCategoria(n.id)];
      const expansivel = !n.folha && depth < PROFUNDIDADE_MAXIMA_SELECAO;
      if (!expansivel || !expandidos.has(n.id)) return linhas;
      if (filhos[n.id] === undefined || carregandoFilhos[n.id]) return [...linhas, linhaCarregando(n.id, depth)];
      return [...linhas, ...linhasHierarquia(filhos[n.id] ?? [], depth + 1)];
    });

  return (
    <>
      <Stack gap={2}>
        <TextField
          label="Nome do cadastro"
          required
          fullWidth
          value={form.nome}
          onChange={(e) => patch({ nome: e.target.value })}
          placeholder="Ex.: Contribuição padrão"
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <DatePicker
            label="Mês de referência"
            views={["year", "month"]}
            format="MM/YYYY"
            value={paraDayjs(form.mesReferencia)}
            onChange={(data) => patch({ mesReferencia: deDayjs(data) })}
            slotProps={{ textField: { required: true, fullWidth: true } }}
            sx={{ maxWidth: { sm: 240 } }}
          />
          <TextField
            select
            label="Situação"
            fullWidth
            sx={{ maxWidth: { sm: 240 } }}
            value={form.ativo ? "Ativo" : "Inativo"}
            onChange={(e) => patch({ ativo: e.target.value === "Ativo" })}
          >
            <MenuItem value="Ativo">Ativo</MenuItem>
            <MenuItem value="Inativo">Inativo</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      <Stack gap={2}>
        <RadioGroup
          row
          value={form.escopoCategorias}
          onChange={(e) => patch({ escopoCategorias: e.target.value as Rascunho["escopoCategorias"] })}
        >
          <FormControlLabel value="todas" control={<Radio />} label="Todas as categorias" />
          <FormControlLabel value="especificas" control={<Radio />} label="Selecionar na hierarquia" />
        </RadioGroup>

        {form.escopoCategorias === "especificas" && (
          <TextField
            size="small"
            fullWidth
            label="Filtrar categorias e produtos"
            value={filtroCategorias}
            onChange={(e) => setFiltroCategorias(e.target.value)}
            placeholder="Busque nos níveis já abertos"
            sx={{ maxWidth: { sm: 360 }, mt: 1.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          sx={{ mt: 1, mb: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            {form.escopoCategorias === "todas"
              ? "Regra única para toda a árvore"
              : categoriasEncontradas
                ? `${categoriasEncontradas.length} resultado(s) · ${form.categorias.length} seleção(ões)`
                : `${form.categorias.length} seleção(ões) na hierarquia`}
          </Typography>
          <Button
            size="small"
            disabled={form.escopoCategorias === "todas" || !!categoriasEncontradas}
            onClick={() => setExpandidos(new Set())}
          >
            Recolher tudo
          </Button>
        </Stack>

        <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CATEGORIA / PRODUTO</TableCell>
                <TableCell>CONTRIBUIÇÃO (%)</TableCell>
                <TableCell>OBSERVAÇÕES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.escopoCategorias === "todas" ? (
                <TableRow sx={{ bgcolor: "background.subtle" }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Todas as categorias e produtos
                    </Typography>
                  </TableCell>
                  <CelulasRegra
                    nome={nomeCategoria(indice, TODAS)}
                    regra={regraDe(form, TODAS)}
                    habilitada
                    herdada={false}
                    onChange={(valores) => alterarRegra(TODAS, valores)}
                  />
                </TableRow>
              ) : categoriasEncontradas ? (
                categoriasEncontradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      Nenhuma categoria ou produto encontrado para “{filtroCategorias.trim()}”.
                    </TableCell>
                  </TableRow>
                ) : (
                  categoriasEncontradas.map((n) => linhaCategoria(n.id, caminhoDe(indice, n.pais)))
                )
              ) : carregandoRaiz ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Carregando a hierarquia do ERP…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : raiz.length === 0 && selecionadasForaDaArvore.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Nenhuma categoria disponível para este cliente.
                  </TableCell>
                </TableRow>
              ) : (
                [
                  ...selecionadasForaDaArvore.map((id) => linhaCategoria(id, caminhoDe(indice, ancestraisDe(indice, id)))),
                  ...linhasHierarquia(raiz),
                ]
              )}
            </TableBody>
          </Table>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Marque as categorias e informe a contribuição de cada uma. A regra de uma categoria vale para seus
          descendentes. Para percentuais diferentes, selecione as subcategorias individualmente.
        </Typography>
      </Stack>

      {erros.length > 0 && (
        <Alert severity="error" onClose={onFecharErros}>
          {erros.join(" ")}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        * Campos obrigatórios
      </Typography>
    </>
  );
}

type ContribuicaoFiltro = {
  nome?: string;
  situacao?: string;
  mesReferencia?: string;
};

const FILTROS_VAZIOS = { nome: "", situacao: "", mesReferencia: "" };

export function MetasContribuicaoTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Contribuicao>();

  const [contribuicoes, setContribuicoes] = useState<Contribuicao[]>([]);
  const [erros, setErros] = useState<string[]>([]);
  const [excluindo, setExcluindo] = useState<Contribuicao | null>(null);
  const [loading, setLoading] = useState(true);
  // Incrementada sempre que uma tentativa de resolverCategorias termina
  // (achando o nome ou não), só para forçar o recálculo de
  // nomesListagemPendentes abaixo — indice sozinho não muda quando o id
  // não é encontrado.
  const [versaoTentativas, setVersaoTentativas] = useState(0);

  const [raizCategorias, setRaizCategorias] = useState<CategoriaMercadologica[]>([]);
  const [filhosCategorias, setFilhosCategorias] = useState<Record<string, CategoriaMercadologica[]>>({});
  const [carregandoRaiz, setCarregandoRaiz] = useState(true);
  const [carregandoFilhos, setCarregandoFilhos] = useState<Record<string, boolean>>({});
  const [indice, setIndice] = useState<Indice>({});
  // Guarda contra pedir duas vezes o mesmo nível: o estado de carregamento só
  // chega no próximo render, e a árvore pode se re-renderizar antes disso.
  const buscasEmAndamento = useRef<Set<string>>(new Set());
  // Mesma guarda que buscasEmAndamento, para não repetir localizarCategorias
  // enquanto a resposta de uma chamada anterior ainda não voltou.
  const resolvendoCategorias = useRef<Set<string>>(new Set());
  // Ids para os quais já tentamos resolverCategorias e a resposta chegou
  // (achada ou não): sem isso, um id apagado do ERP nunca sai do indice
  // como pendente e o spinner da listagem trava para sempre.
  const idsTentados = useRef<Set<string>>(new Set());
  // Trocar de cliente troca a mercadologia inteira: as buscas do cliente
  // anterior que ainda estiverem em voo não podem escrever no estado novo.
  const versaoMercadologia = useRef(0);

  const [inputValues, setInputValues] = useState(FILTROS_VAZIOS);
  const [filters, setFilters] = useState<ContribuicaoFiltro>({});

  useEffect(() => {
    // Sem limpar, os cadastros do cliente anterior seguem na tela e clicáveis
    // até a resposta nova chegar — e para sempre, se ela falhar.
    setLoading(true);
    setContribuicoes([]);
    listContribuicoes(clientId)
      .then((salvas) => setContribuicoes(salvas.map(daApi)))
      .catch(() => notify("Não foi possível carregar os cadastros de contribuição.", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  /** Registra os nós recém-chegados com a trilha de ancestrais do seu pai. */
  const indexar = useCallback((pai: string | null, cats: CategoriaMercadologica[]) => {
    setIndice((prev) => {
      const pais = pai === null ? [] : [...(prev[pai]?.pais ?? []), pai];
      const next = { ...prev };
      cats.forEach((c) => {
        next[c.id] = { nome: c.nome, folha: c.folha, pais };
      });
      return next;
    });
  }, []);

  // O primeiro nível da mercadologia é carregado junto da aba; os demais só
  // quando alguém abre o nó, como o comparativo do flash faz.
  useEffect(() => {
    setCarregandoRaiz(true);
    // Trocar de cliente troca a mercadologia inteira: o que já veio do cliente
    // anterior não vale mais. A raiz também precisa cair — sem isso, se a
    // carga do cliente novo falhar, a árvore volta a mostrar as categorias do
    // anterior e elas ficam selecionáveis.
    versaoMercadologia.current += 1;
    setRaizCategorias([]);
    setFilhosCategorias({});
    setIndice({});
    buscasEmAndamento.current.clear();
    resolvendoCategorias.current.clear();
    idsTentados.current.clear();
    const versao = versaoMercadologia.current;
    listCategorias(clientId)
      .then((cats) => {
        if (versao !== versaoMercadologia.current) return;
        setRaizCategorias(cats);
        indexar(null, cats);
      })
      .catch(() => {
        if (versao === versaoMercadologia.current) notify("Não foi possível carregar as categorias.", "error");
      })
      .finally(() => {
        if (versao === versaoMercadologia.current) setCarregandoRaiz(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const carregarFilhos = useCallback(
    (id: string) => {
      if (buscasEmAndamento.current.has(id)) return;
      buscasEmAndamento.current.add(id);
      setCarregandoFilhos((prev) => ({ ...prev, [id]: true }));
      const versao = versaoMercadologia.current;
      listCategorias(clientId, id)
        .then((cats) => {
          if (versao !== versaoMercadologia.current) return;
          setFilhosCategorias((prev) => ({ ...prev, [id]: cats }));
          indexar(id, cats);
        })
        .catch(() => {
          if (versao !== versaoMercadologia.current) return;
          // Sem os filhos o nó continua aberto e vazio; reabrir tenta de novo.
          buscasEmAndamento.current.delete(id);
          notify("Não foi possível carregar as subcategorias.", "error");
        })
        .finally(() => {
          if (versao === versaoMercadologia.current) setCarregandoFilhos((prev) => ({ ...prev, [id]: false }));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, indexar],
  );

  // Um id de um cadastro salvo pode estar a alguns níveis do topo, num nível
  // que ninguém expandiu ainda — sem isso a árvore não teria como saber qual
  // caminho seguir e a linha ficaria com o código cru no lugar do nome (ver
  // comentário de localizarCategorias). Cada id vira um nó no índice, com
  // `pais` remontado a partir do codigoPai que a busca devolve.
  const resolverCategorias = useCallback(
    (ids: string[]) => {
      const pendentes = ids.filter((id) => !resolvendoCategorias.current.has(id));
      if (pendentes.length === 0) return;
      pendentes.forEach((id) => resolvendoCategorias.current.add(id));
      const versao = versaoMercadologia.current;
      localizarCategorias(clientId, pendentes)
        .then((nodos) => {
          if (versao !== versaoMercadologia.current) return;
          const porCodigo = new Map(nodos.map((n) => [n.id, n]));
          const paisDe = (id: string) => {
            const trilha: string[] = [];
            let atual = porCodigo.get(id)?.codigoPai ?? null;
            while (atual) {
              trilha.unshift(atual);
              atual = porCodigo.get(atual)?.codigoPai ?? null;
            }
            return trilha;
          };
          setIndice((prev) => {
            const next = { ...prev };
            nodos.forEach((n) => {
              next[n.id] = { nome: n.nome, folha: n.folha, pais: paisDe(n.id) };
            });
            return next;
          });
        })
        .catch(() => {
          if (versao === versaoMercadologia.current) notify("Não foi possível localizar as categorias selecionadas.", "error");
        })
        .finally(() => {
          pendentes.forEach((id) => {
            resolvendoCategorias.current.delete(id);
            idsTentados.current.add(id);
          });
          if (versao === versaoMercadologia.current) setVersaoTentativas((v) => v + 1);
        });
    },
    [clientId, notify],
  );

  const mercadologia: Mercadologia = useMemo(
    () => ({
      raiz: raizCategorias,
      filhos: filhosCategorias,
      carregandoRaiz,
      carregandoFilhos,
      indice,
      carregarFilhos,
      resolverCategorias,
    }),
    [raizCategorias, filhosCategorias, carregandoRaiz, carregandoFilhos, indice, carregarFilhos, resolverCategorias],
  );

  /** Ids de todos os cadastros da listagem, independente da paginação/filtro. */
  const idsDaListagem = useMemo(() => {
    const ids = new Set<string>();
    contribuicoes.forEach((c) => idsComRegra(c).forEach((id) => id !== TODAS && ids.add(id)));
    return ids;
  }, [contribuicoes]);

  // Sem isso a listagem mostra o código cru até alguém abrir o cadastro: a
  // raiz carregada junto da aba só cobre o N1, e ids mais fundos (N2/produto)
  // ficam sem nome até o form da linha resolvê-los individualmente. Calculado
  // direto no render (não via effect+state) para não deixar passar um frame
  // com o código cru antes do spinner "pegar" a resolução em andamento.
  const nomesListagemPendentes = useMemo(
    () => [...idsDaListagem].some((id) => !indice[id] && !idsTentados.current.has(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsDaListagem, indice, versaoTentativas],
  );

  useEffect(() => {
    const faltantes = [...idsDaListagem].filter((id) => !indice[id] && !idsTentados.current.has(id));
    if (faltantes.length > 0) resolverCategorias(faltantes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsDaListagem, indice, resolverCategorias, versaoTentativas]);

  const filtrados = useMemo(() => {
    const termo = normalizar(filters.nome ?? "");
    return contribuicoes.filter((c) => {
      if (termo && !normalizar(c.nome).includes(termo)) return false;
      if (filters.situacao && c.ativo !== (filters.situacao === "ativo")) return false;
      if (filters.mesReferencia && c.mesReferencia !== filters.mesReferencia) return false;
      return true;
    });
  }, [contribuicoes, filters]);

  const paginados = useMemo(
    () => filtrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtrados, page, rowsPerPage],
  );

  // Excluir os últimos registros pode deixar a página corrente vazia.
  useEffect(() => {
    if (page > 0 && paginados.length === 0) setPage(0);
  }, [page, paginados.length, setPage]);

  const abrirNovo = () => {
    setErros([]);
    handleCreate();
  };

  const abrirEdicao = (contribuicao: Contribuicao) => {
    setErros([]);
    handleEdit(contribuicao);
  };

  // Reaproveita o mesmo modal de edição, mas sem id: ao salvar, vira um
  // cadastro novo em vez de sobrescrever o original. O mês some para obrigar
  // a escolha do mês novo antes de salvar.
  const abrirDuplicacao = (contribuicao: Contribuicao) => {
    setErros([]);
    handleEdit({ ...contribuicao, id: "", mesReferencia: "" });
  };

  const onSave = async (values: Partial<Contribuicao>) => {
    const rascunho: Rascunho = { ...rascunhoVazio(), ...values };
    const problemas = validar(rascunho, indice);
    setErros(problemas);
    if (problemas.length) return;

    // editing pode estar preenchido sem id numa duplicação: nesse caso o
    // cadastro é novo, tanto para o back-end quanto para a lista local.
    const idOriginal = editing?.id || undefined;

    let salva: Contribuicao;
    try {
      // A resposta traz o id gerado no insert, então é ela que entra na lista.
      salva = daApi(await saveContribuicao(paraApi(rascunho, idOriginal), clientId));
    } catch (err: any) {
      notify(err?.message || "Não foi possível salvar o cadastro de contribuição.", "error");
      return;
    }

    setContribuicoes((prev) => (idOriginal ? prev.map((c) => (c.id === idOriginal ? salva : c)) : [...prev, salva]));
    notify("Cadastro de contribuição gravado.", "success");
    handleClose();
  };

  const onDeleteConfirm = async () => {
    if (!excluindo) return;
    // Fecha a confirmação antes de chamar o back: se falhar, a lista fica
    // como está e o erro chega pela notificação.
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await deleteContribuicao(alvo.id, clientId);
    } catch (err: any) {
      notify(err?.message || "Não foi possível excluir o cadastro de contribuição.", "error");
      return;
    }
    setContribuicoes((prev) => prev.filter((c) => c.id !== alvo.id));
    notify("Cadastro de contribuição excluído.", "success");
  };

  const applyFilters = () => {
    setFilters({
      nome: inputValues.nome || undefined,
      situacao: inputValues.situacao || undefined,
      mesReferencia: inputValues.mesReferencia || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues(FILTROS_VAZIOS);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (loading || nomesListagemPendentes) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Percentual de contribuição de cada categoria. Cada cadastro gravado
          fica salvo na configuração de cálculo.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNovo} sx={{ flexShrink: 0 }}>
          Novo cadastro
        </Button>
      </Stack>

      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.nome}
          onChange={(e) => setInputValues((prev) => ({ ...prev, nome: e.target.value }))}
        />
        <TextField
          select
          label="Situação"
          size="small"
          fullWidth
          value={inputValues.situacao}
          onChange={(e) => setInputValues((prev) => ({ ...prev, situacao: e.target.value }))}
        >
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="ativo">Ativo</MenuItem>
          <MenuItem value="inativo">Inativo</MenuItem>
        </TextField>
        <DatePicker
          label="Mês de referência"
          views={["year", "month"]}
          format="MM/YYYY"
          value={paraDayjs(inputValues.mesReferencia)}
          onChange={(data) => setInputValues((prev) => ({ ...prev, mesReferencia: deDayjs(data) }))}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </FilterModal>

      <DataTable
        data={paginados}
        columns={[
          { key: "ativo", label: "Ativo", render: (contribuicao) => <Checkbox checked={contribuicao.ativo} disabled /> },
          { key: "nome", label: "Cadastro" },
          { key: "mesReferencia", label: "Mês de referência", render: (contribuicao) => rotuloMes(contribuicao.mesReferencia) },
          { key: "contribuicao", label: "Contribuição", render: (contribuicao) => rotuloInfluencia(contribuicao, indice) },
        ]}
        total={filtrados.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(contribuicao) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={() => abrirEdicao(contribuicao)} color="primary" aria-label={`Editar ${contribuicao.nome}`}>
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => abrirDuplicacao(contribuicao)}
              color="primary"
              aria-label={`Duplicar ${contribuicao.nome} para outro mês`}
            >
              <ContentCopy />
            </IconButton>
            <IconButton onClick={() => setExcluindo(contribuicao)} color="error" aria-label={`Excluir ${contribuicao.nome}`}>
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      {filtrados.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {contribuicoes.length === 0
            ? "Nenhum cadastro de contribuição gravado."
            : "Nenhum cadastro encontrado para os filtros aplicados."}
        </Typography>
      )}

      {/* Montado só enquanto aberto para que cada abertura recomece do registro atual. */}
      {open && (
        <CrudModal<Contribuicao, Omit<ContribuicaoFormProps, "values" | "setValues" | "isEditing">>
          open
          onClose={handleClose}
          title={editing?.id ? "Editar cadastro de contribuição" : "Novo cadastro de contribuição"}
          initialData={editing}
          onSubmit={onSave}
          Form={ContribuicaoForm}
          extraFormProps={{ mercadologia, erros, onFecharErros: () => setErros([]) }}
        />
      )}

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de cadastro de contribuição"
        text={`Tem certeza que deseja excluir ${excluindo?.nome ?? "o registro"}?`}
        btnText="Confirmar"
      />
    </Box>
  );
}
